import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

import {
  Items,
  PlanParam,
  ProductParamInput,
} from 'containers/ProductRegist/hooks/types';

import Column from 'components/common/Column';
import Text from 'components/common/Text';
import Row from 'components/common/Row';
import SelectDateModal from './components/SelectDateModal';
import { ICON_PLUS_WHITE, ICON_PRODUCT_CALENDAR } from 'assets/icons';
import { OptionSheetType, useCalendarHooks } from './hooks/useCalenderHooks';
import HackleManager from 'utils/hackle';
import CustomCalendarV2 from 'components/Calendar/CustomCalendarV2';
import BasicButton from 'components/Button/BasicButton';
import CommonImage from 'components/common/CommonImage';
import { EXCEPT_HEIGHT } from 'containers/ProductRegist';
import DateAdditionalModal from './components/DateAdditionalModal';
import { IconRefresh } from 'assets/icons';
import ConfirmDialog from 'components/Modal/ConfirmDialog';

const SelectDateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px 12px;

  border-radius: 12px;
  background: #f7f7f8;
`;

const CheckedDateContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  position: absolute;
  left: 0;
  bottom: ${props => (props.$isMobile ? '100px' : '32px')};
  width: 100%;
  pointer-events: none;
  z-index: 15;
`;

interface ChapterFourIProps {
  isMobile: boolean;
  height: number;
  productParamInput: ProductParamInput;
  items: Items[];
  handleOnChangeProductParamInput: (key: string, value: any) => void;
}

const ChapterFour: React.FC<ChapterFourIProps> = props => {
  const {
    isMobile,
    height,
    productParamInput,
    items,
    handleOnChangeProductParamInput,
  } = props;

  const {
    scheduleItem,
    optionSheetType,
    checkedDate,
    isOpenOptionBottomSheet,
    isOpenSelectDateModal,
    isOpenDateAdditionalModal,
    isOpenResetDialog,
    setCheckedDate,
    setIsOpenSelectDateModal,
    handleChangeScheduleItem,
    handleClearCalenderData,
    handleResetCheckedDate,
    handleDateSelect,
    handleIsOpenUpdateOptionSheet,
    handleInitScheduleItem,
    handleGetSpecificDaysInRange,
    handleSetScheduleItem,
    handleCloseDateAdditionalModal,
    handleSetIsOpenResetDialog,
    handleResetScheduleItem,
  } = useCalendarHooks(productParamInput, handleOnChangeProductParamInput);

  const params = useParams<{ id: string }>();
  const isAbleResetSchedule = isEmpty(productParamInput.frip?.schedules);

  useEffect(() => {
    HackleManager.logCreateStepTwoScheduleSettingView(
      params.id !== 'draft' ? Number(params.id) : undefined,
    );
  }, []);

  const renderScheduleForm = (): JSX.Element => {
    return (
      <React.Fragment>
        {isOpenDateAdditionalModal && (
          <DateAdditionalModal
            isMobile={isMobile}
            productParamInput={productParamInput}
            scheduleItem={scheduleItem}
            checkedDate={checkedDate}
            optionSheetType={optionSheetType}
            onClose={handleCloseDateAdditionalModal}
            handleSetScheduleItem={handleSetScheduleItem}
            setCheckedDate={setCheckedDate}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
            handleClearCalenderData={handleClearCalenderData}
            handleChangeScheduleItem={handleChangeScheduleItem}
            handleInitScheduleItem={handleInitScheduleItem}
          />
        )}
        {isOpenSelectDateModal && (
          <SelectDateModal
            isMobile={isMobile}
            onClose={() => setIsOpenSelectDateModal(false)}
            handleGetSpecificDaysInRange={handleGetSpecificDaysInRange}
          />
        )}
      </React.Fragment>
    );
  };

  const renderSelectDateForm = (): JSX.Element => {
    if (isMobile) {
      return (
        <Row gap="20px">
          <Column
            gap="4px"
            style={{ width: 'auto', whiteSpace: 'nowrap' }}
            onClick={handleDateSelect}
          >
            <CommonImage src={ICON_PRODUCT_CALENDAR} />
            <Text color="#989BA2" weight={isMobile ? 600 : 500}>
              일괄 선택
            </Text>
          </Column>
          <Column
            gap="4px"
            style={{ width: 'auto', whiteSpace: 'nowrap' }}
            onClick={() => handleSetIsOpenResetDialog(true)}
          >
            <IconRefresh color={isAbleResetSchedule ? '#DBDCDF' : '#989BA2'} />
            <Text
              color={isAbleResetSchedule ? '#DBDCDF' : '#989BA2'}
              weight={isMobile ? 600 : 500}
            >
              초기화
            </Text>
          </Column>
        </Row>
      );
    }

    return (
      <Row gap="8px">
        <SelectDateButton onClick={handleDateSelect}>
          <Text color="#414B56" size="14px" style={{ whiteSpace: 'nowrap' }}>
            날짜 일괄 선택하기
          </Text>
        </SelectDateButton>
        <SelectDateButton onClick={() => handleSetIsOpenResetDialog(true)}>
          <Text
            color={isAbleResetSchedule ? '#dbdcdf' : '#414B56'}
            size="14px"
            style={{ whiteSpace: 'nowrap' }}
          >
            일정 초기화
          </Text>
        </SelectDateButton>
      </Row>
    );
  };

  const renderContents = (): JSX.Element => {
    if (isMobile) {
      return (
        <React.Fragment>
          <Column gap="8px" align="flex-start">
            <Row
              justifyContent="space-between"
              style={{
                position: 'sticky',
                top: 0,
                padding: '8px 20px 24px',
                width: '100%',
                background: '#fff',
                zIndex: 8,
              }}
            >
              <Column gap="4px" align="flex-start">
                <Row
                  gap="12px"
                  justifyContent="flex-start"
                  style={{ flexWrap: 'wrap' }}
                >
                  <Text
                    color="#1b1c1e"
                    size="24px"
                    weight={isMobile ? 500 : 400}
                  >
                    일정을 추가해 주세요
                  </Text>
                  <Text
                    color="#989BA2"
                    size="16px"
                    weight={isMobile ? 500 : 400}
                  >
                    중복 선택이 가능해요
                  </Text>
                </Row>
              </Column>
              {renderSelectDateForm()}
            </Row>
            <CustomCalendarV2
              isMobile={isMobile}
              calendarList={productParamInput.frip?.schedules}
              checkedDate={checkedDate}
              setCheckedDate={setCheckedDate}
              handleInitScheduleItem={handleInitScheduleItem}
              handleIsOpenUpdateOptionSheet={handleIsOpenUpdateOptionSheet}
            />
          </Column>
          {renderScheduleForm()}
        </React.Fragment>
      );
    }

    return (
      <Row
        align="flex-start"
        style={{
          width: '100%',
        }}
      >
        <Column
          align="flex-start"
          justifyContent="flex-start"
          style={{
            padding: '0 40px',
            minWidth:
              isOpenOptionBottomSheet || checkedDate.length > 0
                ? '450px'
                : '100%',
            maxHeight: `${height - EXCEPT_HEIGHT}px`,
            overflowY: 'scroll',
          }}
        >
          <Row
            justifyContent="space-between"
            style={{
              position: 'sticky',
              top: 0,
              padding: '32px 0',
              width: '100%',
              background: '#fff',
              zIndex: 3,
            }}
          >
            <Row
              gap="12px"
              justifyContent="flex-start"
              style={{ flexWrap: 'wrap' }}
            >
              <Text
                color="#1b1c1e"
                size="24px"
                weight={400}
                style={{ lineHeight: '32px' }}
              >
                날짜를 선택 후 일정을 추가해 주세요
              </Text>
              <Text color="#989BA2" size="16px" weight={400}>
                중복 선택이 가능해요
              </Text>
            </Row>
            {renderSelectDateForm()}
          </Row>
          <CustomCalendarV2
            isMobile={isMobile}
            calendarList={productParamInput.frip?.schedules}
            checkedDate={checkedDate}
            setCheckedDate={setCheckedDate}
            handleInitScheduleItem={handleInitScheduleItem}
            handleIsOpenUpdateOptionSheet={handleIsOpenUpdateOptionSheet}
          />
        </Column>
        {renderScheduleForm()}
      </Row>
    );
  };

  return (
    <>
      {renderContents()}
      {!isEmpty(checkedDate) && (
        <CheckedDateContainer $isMobile={isMobile}>
          <BasicButton
            content="날짜 선택 해제"
            style={{
              padding: '12px 16px',
              background: '#EAEBEC',
              border: 'none',
              fontSize: '16px',
              fontWeight: 600,
              color: '#414B56',
            }}
            onClick={handleResetCheckedDate}
          />
          <BasicButton
            content={
              <Row gap="4px">
                <CommonImage src={ICON_PLUS_WHITE} />
                <Text color="#fff" size="16px" weight={600}>
                  선택한 날짜에 일정 추가하기
                </Text>
              </Row>
            }
            style={{
              padding: '12px 16px',
              background: '#0071E9',
              border: 'none',
            }}
            onClick={() =>
              handleIsOpenUpdateOptionSheet({
                isOpen: true,
                sheetType: OptionSheetType.CREATE,
              })
            }
          />
        </CheckedDateContainer>
      )}
      {isOpenResetDialog && (
        <ConfirmDialog
          title="등록한 일정을 초기화할까요?"
          description="일정은 저장되지 않고 삭제됩니다."
          onAcceptText="네, 초기화 할게요"
          onCloseText="취소"
          onClose={() => {
            handleSetIsOpenResetDialog(false);
          }}
          onAccept={handleResetScheduleItem}
        />
      )}
    </>
  );
};

export default ChapterFour;
